import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Tracks whether a scrollable element's content exceeds its max-height — i.e.
 * whether the list actually scrolls. Attach the returned `ref` to the scroll
 * container and read `overflows` to decide whether affordances like a search
 * field are worth showing (there's nothing to filter when everything fits).
 *
 * The threshold is the real rendered height vs. the container's max-height, not a
 * hardcoded item count, so it stays correct as row height, font, or max-height change.
 *
 * A `ResizeObserver` re-measures once the element is actually sized — the popover
 * that hosts the list isn't laid out at mount time, so a one-shot measurement reads
 * "fits" and never corrects itself. Overflow only ever latches on; it's recomputed
 * from scratch when `itemCount` changes. That way an active filter (which shrinks the
 * rendered list well below the max-height) can't flip the result back and yank the
 * search field away mid-type — overflow is a property of the full list, not the
 * filtered view.
 *
 * This does not compose `@gonfalon/dom`'s `useResizeObserver` / `useElementOverflow`:
 * those attach the observer in an effect keyed on a stable object ref, so a hook held
 * by a parent component never re-observes a list that mounts later inside a portaled
 * popover. The observer is instead attached from the callback ref, at the moment the
 * list element mounts.
 */
export function useListOverflow(itemCount: number) {
  const [overflows, setOverflows] = useState(false);
  const nodeRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const measure = useCallback(() => {
    const el = nodeRef.current;
    if (el && el.scrollHeight - el.clientHeight > 1) {
      setOverflows(true);
    }
  }, []);

  const ref = useCallback(
    (node: HTMLElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      nodeRef.current = node;
      if (node) {
        measure();
        if (typeof ResizeObserver !== 'undefined') {
          const observer = new ResizeObserver(() => measure());
          observer.observe(node);
          observerRef.current = observer;
        }
      }
    },
    [measure],
  );

  // A changed list can overflow differently, so recompute from scratch.
  useEffect(() => {
    setOverflows(false);
    measure();
  }, [itemCount, measure]);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return { ref, overflows };
}
